import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { IMetricValue, IQueryFilter } from '../type'

export interface IMetricData {
  [metricCode: string]: IMetricValue
}

export interface ILocationGroupData {
  locationGroupId: number
  startDate: string
  endDate: string
  metricData: IMetricData | null
}

const gqlQuery = gql`
  query ListLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    listLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        startDate
        endDate
        locationGroupId
        metricData
        metricSummaryData
      }
    }
  }
`

function useListLocationGroupMetricValues(
  startDate: string,
  endDate: string,
  queryFilter: IQueryFilter,
  metricCodes: string[],
) {
  const { data, loading, error } = useQuery(gqlQuery, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_group_ids: queryFilter?.locationGroupIds,
        intersected_location_group_ids:
          queryFilter?.intersectedLocationGroupIds,
        metrics: metricCodes,
      },
    },
    skip: !startDate || !endDate || !queryFilter || !metricCodes,
  })

  return {
    isLoading: loading,
    error,
    data: useMemo(() => {
      if (!data?.listLocationGroupMetricValues?.nodes) {
        return null
      }

      const locationGroups: ILocationGroupData[] = []
      const summary: IMetricData = {}

      data.listLocationGroupMetricValues.nodes.forEach((node: any) => {
        const {
          startDate,
          endDate,
          locationGroupId,
          metricData,
          metricSummaryData,
        } = node

        const formattedMetricData: IMetricData = {}
        if (metricData) {
          Object.keys(metricData).forEach((metricCode: string) => {
            const value = metricData[metricCode]
            if (value) {
              formattedMetricData[metricCode] = {
                code: metricCode,
                name: value.name,
                unit: value.unit,
                value: value.value,
              }
            }
          })
        }

        // Add to locationGroupData
        locationGroups.push({
          locationGroupId: parseInt(locationGroupId),
          startDate,
          endDate,
          metricData: formattedMetricData,
        })

        // Add to summaryData if it exists
        if (metricSummaryData) {
          Object.keys(metricSummaryData).forEach((metricCode: string) => {
            const value = metricSummaryData[metricCode]
            if (value) {
              summary[metricCode] = {
                code: metricCode,
                name: value.name,
                unit: value.unit,
                value: value.value,
              }
            }
          })
        }
      })

      return {
        locationGroups,
        summary,
      }
    }, [data, startDate, endDate, queryFilter, metricCodes]),
  }
}

export default useListLocationGroupMetricValues
